"use client"

import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Search, Package, Clock, CheckCircle, AlertCircle } from "lucide-react"
import { trackOrder } from "@/actions/track-order"
import { format } from "date-fns"
import { cn } from "@/lib/utils"

export default function TrackOrderClient({ locale, siteId }: { locale: string; siteId: string }) {
    const isRTL = locale === 'ar'
    const [orderNumber, setOrderNumber] = useState("")
    const [phone, setPhone] = useState("")
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState<any>(null)
    const [error, setError] = useState("")

    const handleTrack = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError("")
        setResult(null)

        try {
            const res = await trackOrder(orderNumber, phone, siteId)
            if (res.success) {
                setResult(res.order)
            } else {
                setError(isRTL ? "لم يتم العثور على الطلب. تأكد من الرقم ورقم الهاتف." : "Order not found. Check order number and phone.")
            }
        } catch (err) {
            setError(isRTL ? "حدث خطأ غير متوقع" : "Unexpected error occurred")
        } finally {
            setLoading(false)
        }
    }

    const getStatusLabel = (status: string) => {
        if (!isRTL) return status.charAt(0).toUpperCase() + status.slice(1)
        switch (status) {
            case 'pending': return 'قيد الانتظار'
            case 'confirmed': return 'مؤكد'
            case 'processing': return 'قيد التجهيز'
            case 'shipped': return 'تم الشحن'
            case 'delivered': return 'تم التوصيل'
            case 'cancelled': return 'ملغى'
            case 'no_answer': return 'لا يجيب'
            case 'postponed': return 'مؤجل'
            case 'returned': return 'مرتجع'
            default: return status
        }
    }

    const steps = ['pending', 'confirmed', 'processing', 'shipped', 'delivered']
    // Handle status mapping to steps index
    const getStepIndex = (status: string) => {
        const index = steps.indexOf(status)
        if (index > -1) return index
        // If status is not in main steps (e.g. cancelled), handle gracefully
        if (status === 'cancelled') return -1
        // Map other statuses if needed
        return 0
    }

    const currentStepIndex = result ? getStepIndex(result.status) : -1

    return (
        <div className="w-full max-w-4xl mx-auto px-4" dir={isRTL ? 'rtl' : 'ltr'}>

            {!result && (
                <div className="w-full max-w-md mx-auto text-center mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <h1 className="text-3xl font-bold mb-3">{isRTL ? 'تتبع طلبك' : 'Track Your Order'}</h1>
                    <p className="text-muted-foreground">{isRTL ? 'أدخل رقم الطلب ورقم الهاتف لمعرفة الحالة' : 'Enter order number and phone to check status'}</p>
                </div>
            )}

            {!result && (
                <Card className="w-full max-w-md mx-auto shadow-xl border-none ring-1 ring-black/5 dark:ring-white/10 overflow-hidden bg-background">
                    <CardContent className="pt-8 pb-8 px-8">
                        <div className="flex justify-center mb-6">
                            <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                                <Search className="h-8 w-8 text-primary" />
                            </div>
                        </div>
                        <form onSubmit={handleTrack} className="space-y-5">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">{isRTL ? 'رقم الطلب' : 'Order Number'}</label>
                                <Input
                                    placeholder="ECORD-..."
                                    value={orderNumber}
                                    onChange={(e) => setOrderNumber(e.target.value)}
                                    required
                                    className="h-11 border-neutral-200 dark:border-neutral-800 focus:ring-primary"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">{isRTL ? 'رقم الهاتف' : 'Phone Number'}</label>
                                <Input
                                    placeholder="05..."
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    required
                                    className="h-11 border-neutral-200 dark:border-neutral-800 focus:ring-primary"
                                />
                            </div>
                            <Button type="submit" className="w-full h-11 text-base font-medium bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20 transition-all duration-300 hover:scale-[1.02]" disabled={loading}>
                                {loading ? (isRTL ? 'جاري البحث...' : 'Searching...') : (
                                    <span className="flex items-center gap-2">
                                        {isRTL ? 'تتبع الآن' : 'Track Now'}
                                    </span>
                                )}
                            </Button>
                        </form>

                        {error && (
                            <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 text-sm rounded-xl flex items-center gap-3 border border-red-100 dark:border-red-900/20">
                                <AlertCircle className="h-5 w-5 shrink-0" />
                                {error}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {result && (
                <div className="space-y-8 animate-in slide-in-from-bottom-8 duration-700 fade-in">
                    {/* Header Status Card */}
                    <div className="flex flex-col md:flex-row gap-6 items-center justify-between bg-background p-6 rounded-2xl border shadow-sm">
                        <div className="flex items-center gap-4">
                            <div className="h-14 w-14 bg-primary/10 rounded-full flex items-center justify-center">
                                <Package className="h-7 w-7 text-primary" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold">{isRTL ? 'تفاصيل الطلب' : 'Order Details'}</h2>
                                <p className="text-sm text-neutral-500 font-mono mt-1">{result.order_number}</p>
                            </div>
                        </div>
                        <div className={cn(
                            "px-5 py-2 rounded-full text-sm font-bold border",
                            result.status === 'delivered' ? "bg-green-50 text-green-700 border-green-200" : "bg-primary/5 text-primary border-primary/20"
                        )}>
                            {getStatusLabel(result.status)}
                        </div>
                    </div>

                    <Card className="shadow-lg border-none ring-1 ring-black/5 dark:ring-white/10 overflow-hidden bg-background">
                        <CardContent className="pt-10 pb-10">

                            {/* Stepper */}
                            <div className="relative mb-16 mx-4 md:mx-12">
                                <div className="absolute top-1/2 left-0 w-full h-1 bg-neutral-100 dark:bg-neutral-800 -translate-y-1/2 rounded-full" />
                                <div
                                    className={cn(
                                        "absolute top-1/2 h-1 bg-primary -translate-y-1/2 rounded-full transition-all duration-1000 ease-out",
                                        isRTL ? "right-0" : "left-0"
                                    )}
                                    style={{ width: `${Math.max(0, (currentStepIndex / (steps.length - 1)) * 100)}%` }}
                                />

                                <div className="relative flex justify-between w-full">
                                    {steps.map((step, idx) => {
                                        const isCompleted = idx <= currentStepIndex
                                        return (
                                            <div key={step} className="flex flex-col items-center gap-2 group">
                                                <div className={cn(
                                                    "w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-[3px] transition-all duration-500 relative z-10 bg-background",
                                                    isCompleted ? "border-primary bg-primary text-primary-foreground shadow-lg shadow-primary/25 scale-110" : "border-neutral-200 dark:border-neutral-800 text-neutral-300"
                                                )}>
                                                    {isCompleted ? <CheckCircle className="h-5 w-5" /> : idx + 1}
                                                </div>
                                                <span className={cn(
                                                    "text-xs md:text-sm font-medium absolute -bottom-10 whitespace-nowrap transition-colors duration-300",
                                                    isCompleted ? "text-primary font-bold" : "text-neutral-400"
                                                )}>
                                                    {getStatusLabel(step)}
                                                </span>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>

                            {/* Timeline */}
                            <div className="mt-16 sm:px-6">
                                <h3 className="font-bold text-lg mb-8 flex items-center gap-2">
                                    <Clock className="h-5 w-5 text-neutral-400" />
                                    {isRTL ? 'سجل النشاط' : 'Activity Log'}
                                </h3>
                                <div className={cn(
                                    "relative space-y-8 pb-2 border-dashed border-neutral-200 dark:border-neutral-800",
                                    isRTL ? "border-r-2 mr-3 pr-8" : "border-l-2 ml-3 pl-8"
                                )}>
                                    {/* Sort and Deduplicate Timeline */}
                                    {(() => {
                                        const timeline = result.order_timeline ? [...result.order_timeline] : []
                                        // Sort Descending
                                        timeline.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

                                        // Remove strictly encoded duplicates (same status, same minute)
                                        const uniqueTimeline = timeline.filter((event: any, index: number, self: any[]) =>
                                            index === self.findIndex((t) => (
                                                t.status === event.status &&
                                                Math.abs(new Date(t.created_at).getTime() - new Date(event.created_at).getTime()) < 60000 // within 1 minute
                                            ))
                                        )

                                        return uniqueTimeline.map((event: any, i: number) => (
                                            <div key={i} className="relative group">
                                                <div className={cn(
                                                    "absolute top-1.5 w-4 h-4 rounded-full border-2 bg-background transition-colors",
                                                    isRTL ? "-right-[41px]" : "-left-[41px]",
                                                    i === 0 ? "border-primary bg-primary" : "border-neutral-300 dark:border-neutral-700"
                                                )} />
                                                <div className="flex flex-col gap-1.5">
                                                    <span className={cn(
                                                        "font-bold text-base",
                                                        i === 0 ? "text-primary" : "text-neutral-900 dark:text-white"
                                                    )}>
                                                        {getStatusLabel(event.status)}
                                                    </span>
                                                    <span className="text-xs text-neutral-500 font-medium">
                                                        {format(new Date(event.created_at), 'PPP p')}
                                                    </span>
                                                    {event.note && (
                                                        <p className="text-sm bg-neutral-50 dark:bg-neutral-900 p-3 rounded-lg text-neutral-600 dark:text-neutral-300 mt-1 border border-neutral-100 dark:border-neutral-800">
                                                            {event.note}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    })()}
                                </div>
                            </div>

                            <div className="mt-8 flex justify-center">
                                <Button
                                    onClick={() => setResult(null)}
                                    variant="outline"
                                    className="text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50"
                                >
                                    {isRTL ? 'تتبع طلب آخر' : 'Track Another Order'}
                                </Button>
                            </div>

                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    )
}
